/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * authRedirectHash — read what Supabase Auth tells us in the redirect URL, so a
 * failed email link stops being a silent dead end. (Backlog item 63.)
 *
 * THE BUG THIS FIXES. GoTrue redirects back with its outcome in the URL
 * *fragment*:
 *
 *   /reset-password#error=access_denied&error_code=otp_expired&error_description=…
 *
 * Nothing in the app read it — a grep for `error_code|error_description|
 * access_denied|otp_expired|location.hash` returned zero matches. So a link that
 * was expired, already used, or superseded by a newer one produced **no message
 * at all**: the user was dropped on whatever page the redirect hit, and if that
 * was `/`, RequireAuth bounced them to `/login`, where the browser autofilled
 * their saved password. Ryan's report — "the reset link just puts me back on
 * /login with nothing to reset" — was exactly this.
 *
 * WHY IT IS CAPTURED AT BOOT rather than read by the page that needs it: the
 * redirect can land anywhere, and `RequireAuth`'s <Navigate> drops the fragment
 * on the way to /login. By the time an auth screen renders, the evidence is
 * gone. So this runs once before React mounts, stashes the outcome, and cleans
 * the URL; any screen can then claim it.
 *
 * sessionStorage, not a module variable, because the recovery flow can involve a
 * full page load between landing and rendering the screen that shows it.
 */

const KEY = "bc.authRedirect";

/** Supabase's codes are precise but not readable. These are the ones a user can
 *  actually hit on a recovery or magic link; anything else falls back to the
 *  provider's own description. */
const MESSAGES = {
  otp_expired:
    "That link has expired. Reset links are valid for one hour — request a new one below.",
  access_denied:
    "That link is no longer valid. Each link works once, and requesting a new one cancels the previous link. Request a fresh one below.",
};

function parse(raw) {
  if (!raw) return null;
  const p = new URLSearchParams(raw.replace(/^[#?]/, ""));
  const error = p.get("error");
  const code = p.get("error_code");
  const description = p.get("error_description");
  const type = p.get("type");
  if (!error && !code && !description && !type) return null;
  return {
    error, code, type,
    // GoTrue percent-encodes and uses + for spaces; URLSearchParams handles both.
    message: MESSAGES[code] || MESSAGES[error] || description || null,
  };
}

/**
 * Call ONCE, before the app renders. Captures an auth outcome from the URL,
 * stores it, and strips it from the address bar so a refresh doesn't replay it.
 */
export function captureAuthRedirect() {
  try {
    // Supabase uses the fragment; some providers/flows use the query string.
    const found = parse(window.location.hash) || parse(window.location.search);
    if (!found) return;

    // A successful recovery link carries type=recovery and NO error — that is a
    // session, not a failure, and supabase-js consumes it itself. Only remember
    // it so the app can route to the set-password screen instead of the hub.
    sessionStorage.setItem(KEY, JSON.stringify(found));

    if (window.location.hash && parse(window.location.hash)) {
      // Leave the path and query intact; only the fragment is ours to clear.
      // history.replaceState avoids a navigation and keeps back-button sanity.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  } catch {
    /* storage blocked (private mode) — the app still works, just without the message */
  }
}

/** Read and clear. Returns null when there is nothing to report. */
export function takeAuthRedirect() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Peek without consuming — for routing decisions that shouldn't eat the message. */
export function peekAuthRedirect() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
